import { useMutation } from '@base-app/web2'
import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_COLLABORATOR, UPDATE_COLLABORATOR } from '../../../graphql/collaborators/mutations'
import { COLLABORATOR_QUERY } from '../../../graphql/collaborators/queries'
import type { Collaborator } from '../../../graphql/collaborators/types'
import { useCollaboratorFunctionList } from '../../../hooks/useCollaboratorFunctionList'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { refetchQuietly } from '../../../lib/apolloRefetch'
import { prependToLists } from '../../../lib/cacheLists'
import {
  collaboratorFunctionErrorMessage,
  collaboratorFunctionErrorReason,
} from '../../../lib/collaboratorFunctionErrors'
import { PASTOR_RANKS, pastorRankOf } from '../../../lib/pastorRank'
import {
  focusFieldById,
  isPastorOrderInvalidError,
  isPastorRankInvalidError,
  isRichTextTooLongError,
  isSortOrderInvalidError,
  MAX_RICH_TEXT_LENGTH,
} from '../../../lib/richText'
import { MAX_SORT_ORDER, parseSortOrder } from '../../../lib/sortOrder'
import { lowResPathFor } from '../../../lib/uploads'
import { isEmail } from '../../../services/url-validation'
import { Button, FormActions } from '../../atoms'
import {
  FormField,
  FormLoadState,
  FunctionCheckboxes,
  ImageUploadField,
  RichTextField,
  SelectField,
  TextareaField,
} from '../../molecules'

const LEADERSHIP_KEY = 'leadership'
// The function whose people are the Pastores list of the app
const PASTORES_KEY = 'integrated'

// What to send for one of the two display orders (Diretoria, Pastores): the number typed; null when an order that
// was set is now empty (a deliberate clear); nothing when it was empty and still is (the API then
// gives a new board member the next free order)
function orderText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

function sortOrderToSend(
  text: string,
  stored: number | null | undefined,
): number | null | undefined {
  const parsed = parseSortOrder(text)
  if (parsed === null) {
    return stored === null || stored === undefined ? undefined : null
  }
  return parsed
}

interface CollaboratorFormFieldsProps {
  collaborator?: Collaborator
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const CollaboratorFormFields = memo(
  ({
    collaborator,
    clientId,
    appId,
    onSuccess,
    onClose,
    skeleton,
  }: CollaboratorFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!collaborator
    const functionList = useCollaboratorFunctionList(clientId, appId)
    const functionsPath = `/clients/${clientId}/apps/${appId}/collaborator-functions`
    const [name, setName] = useState(collaborator?.name ?? '')
    const [role, setRole] = useState(collaborator?.role ?? '')
    const [email, setEmail] = useState(collaborator?.email ?? '')
    const [selectedFunctions, setSelectedFunctions] = useState<string[]>(
      collaborator?.collaborators_functions ?? [],
    )
    // Only functions that still exist count: one deleted elsewhere is dropped from the list on
    // refetch, so it must not stay selected (its checkbox is gone and every save would fail).
    // Only a list the API just answered can say so: a cached list still refreshing may lack a
    // function another admin added, and filtering by it would silently remove that assignment.
    const checkedFunctions = useMemo(() => {
      if (!functionList.settled) {
        return selectedFunctions
      }
      const keys = new Set(functionList.functions.map(fn => fn.key))
      return selectedFunctions.filter(key => keys.has(key))
    }, [selectedFunctions, functionList.settled, functionList.functions])
    const [info, setInfo] = useState(collaborator?.info ?? '')
    const [image, setImage] = useState(collaborator?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [observations, setObservations] = useState(collaborator?.observations ?? '')
    // The Diretoria order: only asked while the leadership function is ticked
    const [sortOrder, setSortOrder] = useState(orderText(collaborator?.sort_order))
    // The order in the Pastores list: only asked while the Pastores (integrated) function is ticked.
    // The two orders are independent; an empty Pastores order means first
    const [pastorOrder, setPastorOrder] = useState(orderText(collaborator?.pastor_order))
    // The Pastores rank: on create it follows the name's title (Pr, Ev, Pb ...) until the admin picks
    // one; on edit it is the stored value
    const [pickedPastorRank, setPickedPastorRank] = useState<string | null>(null)
    const pastorRank =
      pickedPastorRank ?? String(collaborator ? collaborator.pastor_rank : pastorRankOf(name))
    // Memoized: a new array every render would re-render the memoized select
    const pastorRankOptions = useMemo(
      () =>
        PASTOR_RANKS.map(rank => ({
          value: String(rank),
          label: t(`collaborators.form.pastorRank.${rank}`),
        })),
      [t],
    )
    const isLeader = checkedFunctions.includes(LEADERSHIP_KEY)
    // What the save sends for the order (undefined = the stored value stays); the optimistic row
    // shows the same value, so a cleared order does not flash back to the old one
    const sortOrderSent = isLeader
      ? sortOrderToSend(sortOrder, collaborator?.sort_order)
      : undefined
    const isPastor = checkedFunctions.includes(PASTORES_KEY)
    const pastorOrderSent = isPastor
      ? sortOrderToSend(pastorOrder, collaborator?.pastor_order)
      : undefined
    const [errors, setErrors] = useState<{
      name?: string
      email?: string
      info?: string
      sortOrder?: string
      pastorOrder?: string
      pastorRank?: string
    }>({})

    const makeOptimistic = useCallback(
      (id: string): Collaborator => ({
        __typename: 'Collaborator',
        id,
        client_id: clientId,
        application_id: appId,
        name,
        role: role || null,
        email: email || null,
        info: info || null,
        collaborators_functions: checkedFunctions,
        image: image || null,
        image_low_res: lowResPathFor(image),
        observations: observations || null,
        sort_order:
          sortOrderSent === undefined ? (collaborator?.sort_order ?? null) : sortOrderSent,
        pastor_order:
          pastorOrderSent === undefined ? (collaborator?.pastor_order ?? null) : pastorOrderSent,
        pastor_rank: Number(pastorRank),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [
        clientId,
        appId,
        name,
        role,
        email,
        info,
        checkedFunctions,
        image,
        observations,
        pastorRank,
        collaborator?.sort_order,
        sortOrderSent,
        pastorOrderSent,
        collaborator?.pastor_order,
      ],
    )

    // A function deleted elsewhere while the form was open: say so, then reload the options
    const handleError = useCallback(
      (err: Error) => {
        if (isRichTextTooLongError(err)) {
          setErrors(current => ({ ...current, info: t('collaborators.form.infoTooLong') }))
          focusFieldById('col-info')
          return
        }
        if (isPastorOrderInvalidError(err)) {
          setErrors(current => ({ ...current, pastorOrder: t('common.form.sortOrderInvalid') }))
          focusFieldById('col-pastor-order')
          return
        }
        if (isSortOrderInvalidError(err)) {
          setErrors(current => ({ ...current, sortOrder: t('common.form.sortOrderInvalid') }))
          focusFieldById('col-sort-order')
          return
        }
        if (isPastorRankInvalidError(err)) {
          setErrors(current => ({
            ...current,
            pastorRank: t('collaborators.form.pastorRankInvalid'),
          }))
          focusFieldById('col-pastor-rank')
          return
        }
        showToast(collaboratorFunctionErrorMessage(err, t), 'error')
        if (collaboratorFunctionErrorReason(err) === 'FUNCTION_NOT_FOUND') {
          refetchQuietly(functionList.refetch())
        }
      },
      [showToast, t, functionList.refetch],
    )

    const [createCollaborator, { loading: creating }] = useMutation(CREATE_COLLABORATOR, {
      optimisticResponse: { createCollaborator: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createCollaborator
        if (!item) {
          return
        }
        prependToLists(cache, 'collaborators', item, { clientId, applicationId: appId })
      },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const [updateCollaborator, { loading: updating }] = useMutation(UPDATE_COLLABORATOR, {
      optimisticResponse: { updateCollaborator: makeOptimistic(collaborator?.id ?? '') },
      onError: handleError,
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    // A stable identity keeps the memoized checkboxes from re-rendering with the form
    const retryFunctions = useCallback(
      () => refetchQuietly(functionList.refetch()),
      [functionList.refetch],
    )

    const toggleFunction = useCallback((fnId: string) => {
      setSelectedFunctions(prev =>
        prev.includes(fnId) ? prev.filter(f => f !== fnId) : [...prev, fnId],
      )
    }, [])

    const validate = useCallback(() => {
      const errs: {
        name?: string
        email?: string
        info?: string
        sortOrder?: string
        pastorOrder?: string
      } = {}
      if (!name.trim()) {
        errs.name = t('common.form.nameRequired')
      }
      if (email.trim() && !isEmail(email.trim())) {
        errs.email = t('common.form.emailInvalid')
      }
      if (info.length > MAX_RICH_TEXT_LENGTH) {
        errs.info = t('collaborators.form.infoTooLong')
      }
      if (isLeader && parseSortOrder(sortOrder) === undefined) {
        errs.sortOrder = t('common.form.sortOrderInvalid')
      }
      if (isPastor && parseSortOrder(pastorOrder) === undefined) {
        errs.pastorOrder = t('common.form.sortOrderInvalid')
      }
      setErrors(errs)
      // A refusal is announced by moving focus to the field, as for the API refusals
      if (errs.sortOrder) {
        focusFieldById('col-sort-order')
      } else if (errs.pastorOrder) {
        focusFieldById('col-pastor-order')
      }
      return Object.keys(errs).length === 0
    }, [name, email, info, isLeader, sortOrder, isPastor, pastorOrder, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const vars = {
          name,
          role: role || null,
          email: email || null,
          info: info || null,
          collaborators_functions: checkedFunctions,
          image: image || null,
          observations: observations || null,
          // Left out (undefined) = the API keeps the stored value, or gives a new board member the
          // next free order. Sent as null only when the admin cleared an order that was set.
          sort_order: sortOrderSent,
          pastor_order: pastorOrderSent,
          pastor_rank: pickedPastorRank === null ? undefined : Number(pickedPastorRank),
        }
        if (isEdit) {
          updateCollaborator({ variables: { id: collaborator.id, ...vars } })
        } else {
          createCollaborator({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        validate,
        isEdit,
        collaborator?.id,
        pickedPastorRank,
        name,
        role,
        email,
        info,
        checkedFunctions,
        image,
        observations,
        clientId,
        appId,
        updateCollaborator,
        createCollaborator,
        sortOrderSent,
        pastorOrderSent,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          skeleton={skeleton}
          id="col-name"
          label={t('collaborators.form.nameLabel')}
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <FormField
          skeleton={skeleton}
          id="col-role"
          label={t('collaborators.form.roleLabel')}
          name="role"
          value={role}
          onChange={e => setRole(e.target.value)}
        />
        <FormField
          skeleton={skeleton}
          id="col-email"
          label={t('collaborators.form.emailLabel')}
          name="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          type="email"
          error={errors.email}
        />
        <fieldset className="checkbox-group">
          <legend>{t('collaborators.form.functionsLabel')}</legend>
          <FunctionCheckboxes
            functions={functionList.functions}
            selected={checkedFunctions}
            onToggle={toggleFunction}
            loading={functionList.loading}
            failed={!!functionList.error}
            onRetry={retryFunctions}
            manageHref={functionsPath}
            skeleton={skeleton}
          />
        </fieldset>
        {isLeader ? (
          <FormField
            skeleton={skeleton}
            id="col-sort-order"
            label={t('collaborators.form.sortOrderLabel')}
            name="sort_order"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={String(MAX_SORT_ORDER).length}
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
            error={errors.sortOrder}
            hint={t('collaborators.form.sortOrderHint')}
          />
        ) : null}
        {isPastor ? (
          <FormField
            skeleton={skeleton}
            id="col-pastor-order"
            label={t('collaborators.form.pastorOrderLabel')}
            name="pastor_order"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={String(MAX_SORT_ORDER).length}
            value={pastorOrder}
            onChange={e => setPastorOrder(e.target.value)}
            error={errors.pastorOrder}
            hint={t('collaborators.form.pastorOrderHint')}
          />
        ) : null}
        <SelectField
          skeleton={skeleton}
          id="col-pastor-rank"
          label={t('collaborators.form.pastorRankLabel')}
          name="pastor_rank"
          value={pastorRank}
          onChange={setPickedPastorRank}
          options={pastorRankOptions}
          error={errors.pastorRank}
          hint={t('collaborators.form.pastorRankHint')}
        />
        <ImageUploadField
          skeleton={skeleton}
          applicationId={appId}
          id="col-image-file"
          target="collaborators"
          label={t('collaborators.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
        />
        <RichTextField
          skeleton={skeleton}
          id="col-info"
          label={t('collaborators.form.infoLabel')}
          value={info}
          onChange={setInfo}
          error={errors.info}
        />
        <TextareaField
          skeleton={skeleton}
          id="col-observations"
          label={t('collaborators.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading ? t('collaborators.form.saving') : t('collaborators.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

CollaboratorFormFields.displayName = 'CollaboratorFormFields'

interface CollaboratorFormProps extends Omit<CollaboratorFormFieldsProps, 'collaborator'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const CollaboratorForm = memo(({ id, ...props }: CollaboratorFormProps) => {
  const { record, status } = useRecordById(COLLABORATOR_QUERY, id, data => data.collaborator)
  if (status === 'loading') {
    return <CollaboratorFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <CollaboratorFormFields key={id ?? 'new'} collaborator={record} {...props} />
})

CollaboratorForm.displayName = 'CollaboratorForm'
